import React, { useState, useEffect, useRef } from "react";
import { Dropdown, Form } from "react-bootstrap";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

/**
 * MarketingPersonaPicker - A dropdown component for selecting marketing personas via PostgREST API
 *
 * @param {string} value - The selected marketing persona ID
 * @param {function} onChange - Callback when selection changes: (marketingPersonaId, marketingPersonaObject) => void
 * @param {function} onBlur - Callback when field loses focus (optional)
 * @param {boolean} isInvalid - Whether to show error state
 * @param {boolean} isValid - Whether to show valid state
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Whether the dropdown is disabled
 * @param {string} placeholder - Placeholder text when no selection
 */
export default function MarketingPersonaPicker({
  value,
  onChange,
  onBlur,
  isInvalid = false,
  isValid = false,
  className = "",
  disabled = false,
  placeholder = "Select a persona...",
}) {
  const [marketingPersonas, setMarketingPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  useEffect(() => {
    async function fetchMarketingPersonas() {
      try {
        setLoading(true);
        setError(null);

        const url = new URL(`${globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL}/rest/v1/marketing_personas`);

        url.searchParams.set("select", "id,first_name,last_name,email");
        url.searchParams.set("order", "first_name.asc,last_name.asc");

        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            ...authenticatedHeadersHash(),
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch personas: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        setMarketingPersonas(Array.isArray(data) ? data : []);
      } catch (err) {
        // Error fetching personas
        setError(err.message);
        setMarketingPersonas([]);
      } finally {
        setLoading(false);
      }
    }

    fetchMarketingPersonas();
  }, []);

  // Filter personas based on search term
  const filteredPersonas = marketingPersonas.filter((persona) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();
    const fullName = `${persona.first_name} ${persona.last_name}`.toLowerCase();
    const email = (persona.email || "").toLowerCase();

    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  // Handle dropdown toggle and focus search input when opened
  const handleToggle = (dropdownIsOpen) => {
    if (dropdownIsOpen) {
      // Focus search input after dropdown opens
      // Use multiple animation frames to ensure DOM is fully rendered
      const focusInput = () => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        } else {
          // If ref isn't ready yet, try again on next frame
          requestAnimationFrame(focusInput);
        }
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(focusInput);
      });
    } else if (!dropdownIsOpen) {
      // Clear search when dropdown closes
      setSearchTerm("");
    }

    if (onBlur && !dropdownIsOpen) {
      onBlur();
    }
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle keyboard navigation in search input
  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown" && filteredPersonas.length > 0) {
      e.preventDefault();
      // Focus first dropdown item
      const firstItem = e.target.closest(".dropdown-menu")?.querySelector(".dropdown-item:not(.disabled)");
      firstItem?.focus();
    } else if (e.key === "Escape") {
      // Let the dropdown handle closing naturally
      e.target.blur();
    } else if (e.key === "Enter" && filteredPersonas.length === 1) {
      // Auto-select if only one result
      e.preventDefault();
      const singleResult = filteredPersonas[0];
      if (onChange) {
        onChange(String(singleResult.id), singleResult);
      }
    }
  };

  const selectedMember = marketingPersonas.find((member) => String(member.id) === String(value));
  const displayText = selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : placeholder;

  // Determine Bootstrap validation classes
  let validationClass = "";
  if (isInvalid) {
    validationClass = "is-invalid";
  } else if (isValid) {
    validationClass = "is-valid";
  }

  if (loading) {
    return (
      <div className={`form-control bg-white ${validationClass} ${className}`} style={{ color: "#6c757d" }}>
        <i className="fa fa-spinner fa-spin me-2" aria-hidden="true" />
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`form-control bg-white is-invalid ${className}`} style={{ color: "#dc3545" }}>
        <i className="fa fa-exclamation-triangle me-2" aria-hidden="true" />
        Error loading personas
      </div>
    );
  }

  if (marketingPersonas.length === 0) {
    return (
      <div className={`form-control bg-white ${validationClass} ${className}`} style={{ color: "#6c757d" }}>
        <i className="fa fa-info-circle me-2" aria-hidden="true" />
        No personas found
      </div>
    );
  }

  return (
    <div className="position-relative">
      <style>{`
        .dropdown-menu .dropdown-item:focus small,
        .dropdown-menu .dropdown-item.active small {
          color: rgba(255, 255, 255, 0.7) !important;
        }
      `}</style>
      <Dropdown
        onSelect={(eventKey) => {
          if (onChange) {
            if (eventKey === "clear") {
              onChange("", null);
            } else {
              const foundMember = marketingPersonas.find((member) => String(member.id) === String(eventKey));
              onChange(String(eventKey), foundMember);
            }
          }
        }}
        onToggle={handleToggle}
      >
        <Dropdown.Toggle
          as="div"
          className={`form-control d-flex justify-content-between align-items-center bg-white ${validationClass} ${className}`}
          style={{
            border: (() => {
              if (isInvalid) return "1px solid #dc3545";
              if (isValid) return "1px solid #198754";
              return "1px solid #ced4da";
            })(),
            color: selectedMember ? "#212529" : "#6c757d",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <div className="d-flex align-items-center" style={{ overflow: "hidden" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayText}</span>
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="p-0"
          style={{
            maxHeight: "350px",
            width: "100%",
            backgroundColor: "white",
            overflow: "hidden",
          }}
        >
          {/* Sticky Search Input */}
          <div
            className="px-3 py-2 border-bottom bg-light"
            style={{
              position: "sticky",
              top: "0",
              zIndex: 10,
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #dee2e6",
            }}
          >
            <Form.Control
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onClick={(e) => e.stopPropagation()}
              size="sm"
              className="border-0 shadow-none"
              style={{ backgroundColor: "white" }}
            />
          </div>

          {/* Scrollable dropdown items - direct children of Dropdown.Menu */}
          <div
            style={{
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {/* Clear Selection Option */}
            {value && (
              <>
                <Dropdown.Item eventKey="clear" className="text-muted">
                  <i className="fa fa-times me-2" aria-hidden="true" />
                  Clear selection
                </Dropdown.Item>
                <Dropdown.Divider />
              </>
            )}

            {/* Filtered Results */}
            {filteredPersonas.length === 0 && searchTerm.trim() ? (
              <div className="px-3 py-2 text-muted">
                <i className="fa fa-search me-2" aria-hidden="true" />
                No personas found matching &ldquo;{searchTerm}&rdquo;
              </div>
            ) : (
              filteredPersonas.map((member) => (
                <Dropdown.Item
                  key={member.id}
                  eventKey={String(member.id)}
                  active={String(member.id) === String(value)}
                >
                  <div className="d-flex align-items-center">
                    <div className="d-flex flex-column">
                      <div className="fw-medium">
                        {member.first_name} {member.last_name}
                      </div>
                      {member.email && <small className="text-muted">{member.email}</small>}
                    </div>
                  </div>
                </Dropdown.Item>
              ))
            )}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
